import { useState, useEffect } from 'react';

import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';

import CustomImage from '@/components/CustomImage';
import CustomModal from '@/components/CustomModal';
import { ERoleAccess } from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useFormatText } from '@/hooks/useFormatText';

interface IImageModalProps {
  activeImageIndex: number;
  setActiveImageIndex: (index: number | null) => void;
  assets: string[];
}

const ImageModal = ({
  activeImageIndex,
  setActiveImageIndex,
  assets,
}: IImageModalProps) => {
  const { hasAccess } = useAccess();

  const { formatText } = useFormatText();
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 50, y: 50 });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (activeImageIndex === null || !assets) return;

      if (e.key === 'ArrowLeft' && activeImageIndex > 0) {
        setActiveImageIndex(activeImageIndex - 1);
      } else if (
        e.key === 'ArrowRight' &&
        activeImageIndex < assets.length - 1
      ) {
        setActiveImageIndex(activeImageIndex + 1);
      } else if (e.key === 'Escape') {
        setActiveImageIndex(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeImageIndex, assets]);

  useEffect(() => {
    setScale(1);
    setPosition({ x: 50, y: 50 });
  }, [activeImageIndex]);

  const handleZoom = (e: React.MouseEvent<HTMLDivElement>) => {
    if (scale > 1) {
      setScale(1);
      setPosition({ x: 50, y: 50 });
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setPosition({ x, y });
      setScale(2.5);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (scale === 1) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setPosition({ x, y });
  };

  if (!hasAccess(ERoleAccess.SUPPORT_QUERIES)) return null;

  return (
    <CustomModal
      setIsOpen={(isOpen) => !isOpen && setActiveImageIndex(null)}
      className="max-w-[95vw]! max-h-[95vh]! h-[95vh]! w-full! relative"
      childClassName="p-0! h-[calc(100vh-9rem)]!"
      isHeader={true}
      heading={formatText('support.details.evidence-preview')}
      onClose={() => setActiveImageIndex(null)}
    >
      <div className="flex flex-col items-center justify-center p-4 md:p-6 h-full w-full relative">
        {assets?.length > 1 && (
          <>
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white hover:bg-neutral-light-100 p-3 rounded-full shadow-[0_4px_20px_0_rgba(0,0,0,0.1)] text-neutral-primary transition-all disabled:opacity-30 disabled:cursor-not-allowed group"
              disabled={activeImageIndex === 0}
              aria-label="Previous image"
              onClick={() => setActiveImageIndex(activeImageIndex - 1)}
            >
              <IconChevronLeft
                size={28}
                className="group-hover:scale-110 transition-transform"
              />
            </button>
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white hover:bg-neutral-light-100 p-3 rounded-full shadow-[0_4px_20px_0_rgba(0,0,0,0.1)] text-neutral-primary transition-all disabled:opacity-30 disabled:cursor-not-allowed group"
              disabled={activeImageIndex === assets.length - 1}
              aria-label="Next image"
              onClick={() => setActiveImageIndex(activeImageIndex + 1)}
            >
              <IconChevronRight
                size={28}
                className="group-hover:scale-110 transition-transform"
              />
            </button>
          </>
        )}

        <div
          className="flex items-center justify-center w-full h-full overflow-hidden rounded-lg bg-neutral-light-50"
          onMouseMove={handleMouseMove}
          onClick={handleZoom}
        >
          <div
            className="w-full h-full flex items-center justify-center transition-transform duration-200 ease-out"
            style={{
              transform: `scale(${scale})`,
              transformOrigin: `${position.x}% ${position.y}%`,
              cursor: scale > 1 ? 'zoom-out' : 'zoom-in',
            }}
          >
            <CustomImage
              src={assets[activeImageIndex]}
              alt="Evidence Large"
              className="max-h-full max-w-full w-auto h-auto object-contain selection:bg-transparent"
            />
          </div>
        </div>

        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur-sm text-neutral-primary px-4 py-1.5 rounded-full text-sm font-bold border border-neutral-light-200 shadow-sm z-10">
          {activeImageIndex + 1} / {assets.length}
        </div>
      </div>
    </CustomModal>
  );
};

export default ImageModal;
